import type { DateTime } from "luxon";
import type { CSSProperties } from "react";
import { NURSE_ROW_HIGHLIGHT } from "../../../theme/tokens";
import {
	type CategoricalColor,
	type JobColorResolver,
	NEUTRAL_CATEGORICAL_COLOR,
	type RoleGroupColorResolver,
} from "../../../utils/categoricalColors";
import type { NurseFullRole } from "../../../utils/models";
import { hexToOpacity } from "../../../utils/utilFunctions";

export const DAY_WIDTH: CSSProperties["width"] = "calc(100% / 7)";
export const TOTAL_WIDTH = "100%";
export const WEEKLY_GAP_VIEW_ROOT_ID = "weekly-gap-view-root";
export const NURSE_HIGHLIGHT_CLASS = "weekly-gap-nurse-highlight";
export const NURSE_HIGHLIGHT_BG = NURSE_ROW_HIGHLIGHT;

// Brand-tinted hover/border color — follows the user's chosen theme.
// Uses the lighter shade so it stays bright/lavender-feeling even when the
// user keeps the default desaturated brand purple.
export const purpleHEX: CSSProperties["color"] = "var(--inhouse-primary-light)";
// 50% alpha overlay of the lighter shade, for soft borders.
export const purpleHEXOpacity: CSSProperties["color"] = "var(--inhouse-primary-light-alpha-50)";

// Fixed row heights for the virtualized grid. Calibrated against the rendered components:
//   - role-header: 18px content + 4px top padding (py: 0.5).
//   - time-title: ~36px (py: 0.5 with 14px label + 12px day + badge) plus 1px border-bottom.
//   - location-header: bgcolor row, py: 0.5, badges height ~24px.
//   - nurse-slot: py: 2px + 12px text + flex stretch — comfortable at 36 for a single-line cell.
//     A nurse-slot row spans all 7 day columns at one slotIdx (see NurseSlotRow), and
//     NurseCellContent appends a second line (the shift's actual hours, ~19px measured line box)
//     whenever ANY of those 7 cells is a partial shift relative to its time-title group
//     (`nurse.showHours`, computed in useWeeklyGapView from `shiftStart/End !== group.start/endMin`).
//     react-window absolutely-positions each row at exactly this estimate with no clipping, so a
//     two-line cell squeezed into the 36px single-line box bled ~7px into the row below (visible as
//     overflow into the next location's header/rows). `nurseSlotRowNeedsHoursLine` (FlatRowRenderer.tsx)
//     detects the two-line case per row; only those rows get `NURSE_SLOT_HOURS_LINE_EXTRA_PX` added on
//     top of the compact base, so ordinary single-line rows keep the tighter 36px.
export type FlatRowKind = "role-header" | "time-title" | "location-header" | "nurse-slot";
export const ROW_HEIGHT_BY_KIND: Record<FlatRowKind, number> = {
	"role-header": 26,
	"time-title": 44,
	"location-header": 36,
	"nurse-slot": 36,
};

// Extra height a "nurse-slot" row needs when it renders NurseCellContent's second (actual-hours)
// stacked line — measured ~19.2px rendered line box for the 12px hours label; rounded up to 20 for
// headroom. Added on top of ROW_HEIGHT_BY_KIND["nurse-slot"] only for rows that need it (see above).
export const NURSE_SLOT_HOURS_LINE_EXTRA_PX = 20;

// Top spacing rendered ABOVE each role-header row except the first — the
// gap that visually separates one role section from the next. Matches the
// Magic Patterns mockup's `mb-6` between role-section cards.
export const ROLE_HEADER_TOP_GAP_PX = 24;

/** Band opacity over the section's fill — pale enough for a full-width header, still the section's hue. */
const SECTION_BAND_OPACITY = 0.45;

/** What decides a section's colour: its job when it is a job section, its role or role group otherwise. */
export type SectionIdentity = { shortRole: string; isJobSection: boolean; jobId?: string };

/**
 * A section's tone, shared by this view's header row and the mobile gaps-first home, so one section reads the
 * same on both screens. `band` is the header's translucent tint, `text` its label ink, `badge` the solid chip
 * fill (dark ink goes on it), and `dot` the section's mark on the gap line and in the colour key.
 */
export type SectionTone = { band: string; text: string; badge: string; dot: string };

export function getSectionTone(
	section: SectionIdentity,
	roleGroups: Readonly<Record<string, readonly string[]>>,
	jobColor: JobColorResolver,
	roleGroupColor: RoleGroupColorResolver,
): SectionTone {
	const color = sectionColor(section, roleGroups, jobColor, roleGroupColor);
	return { band: hexToOpacity(color.fill, SECTION_BAND_OPACITY), text: color.ink, badge: color.fill, dot: color.dot };
}

const sectionColor = (
	section: SectionIdentity,
	roleGroups: Readonly<Record<string, readonly string[]>>,
	jobColor: JobColorResolver,
	roleGroupColor: RoleGroupColorResolver,
): CategoricalColor => {
	if (!section.isJobSection) return roleGroupColor(section.shortRole, roleGroups);
	// DEFAULT: a job section is always built from one job, so a missing id means a section assembled from a
	// label alone (an open request's job name) — neutral rather than guessing which job it is.
	if (section.jobId === undefined) return NEUTRAL_CATEGORICAL_COLOR;
	return jobColor(section.jobId);
};

export type MissingSlotInfo = {
	dayDate: DateTime;
	locationName: string;
	startMin: number;
	endMin: number;
	roles: NurseFullRole[];
	jobId?: string;
	existingReqId?: string;
};

export const toggleNurseHighlight = (nurseId: number, on: boolean): void => {
	// Query document-wide so highlights also reach nurse rows rendered outside the gap view
	// (e.g., the costly-staff list inside a DayViewItem).
	const cells = document.querySelectorAll(`[data-nurse-id="${nurseId}"]`);
	for (const cell of cells) {
		if (on) cell.classList.add(NURSE_HIGHLIGHT_CLASS);
		else cell.classList.remove(NURSE_HIGHLIGHT_CLASS);
	}
};
